import type { FaultData } from '../api/types';
import { formatDTCCode, getFaultSeverity, getFaultStateLabel, faultLabel } from '../utils/dtc';
import { escapeHtml } from '../utils/html';

// Card builders for the redesigned Fault Modal. These intentionally live apart from
// ./fault-cards.ts (the compact cards used by the Vehicle Health side panel) so the
// modal can have a richer, class-driven layout without changing the side panel.

function stateBadge(fault: FaultData): string {
  const st = getFaultStateLabel(fault);
  if (!st) return '';
  return `<span class="fm-state"><span class="fm-state-dot" style="background:${st.color};"></span>${st.label}</span>`;
}

/** Rich vehicle/ECU fault card (action-required styling). */
export function buildVehicleFaultCardHTML(fault: FaultData): string {
  const dtc = formatDTCCode(fault.diagnostic?.code ?? null);
  const name = fault.diagnostic?.name;
  const isUnknownName = !name || name.toLowerCase().includes('unknown') || name.startsWith('**');
  const displayName = !isUnknownName
    ? name
    : dtc ? 'OEM proprietary — scanner required' : 'Unknown fault';
  const severity = getFaultSeverity(fault);

  const top = `<div class="fm-card-top">
      <div class="fm-card-id">
        ${dtc ? `<span class="fm-dtc">${dtc}</span>` : ''}
        <div class="fm-faultname">${escapeHtml(displayName)}</div>
      </div>
      <div class="fm-card-badges">
        ${severity ? `<span class="fm-badge" style="color:${severity.color};background:${severity.bg};border-color:${severity.color}3a;">${severity.label}</span>` : ''}
        ${stateBadge(fault)}
      </div>
    </div>`;

  let body = '';
  if (fault.faultDescription || fault.effectOnComponent || fault.recommendation) {
    body += '<div class="fm-body">';
    if (fault.faultDescription) {
      body += `<div class="fm-desc">${escapeHtml(fault.faultDescription)}</div>`;
    }
    if (fault.effectOnComponent) {
      body += `<div><div class="fm-field-l">Effect on component</div><div class="fm-field-v">${escapeHtml(fault.effectOnComponent)}</div></div>`;
    }
    if (fault.recommendation) {
      body += `<div class="fm-rec"><div class="fm-field-l">Recommended action</div><div class="fm-field-v">${escapeHtml(fault.recommendation)}</div></div>`;
    }
    body += '</div>';
  }

  let footer = '';
  if (fault.riskOfBreakdown) {
    footer += `<div class="fm-risk">Risk of breakdown: <b>${escapeHtml(fault.riskOfBreakdown)}</b></div>`;
  }
  const logged = (fault.count ?? 0) > 1 ? ` · Logged ${fault.count}×` : '';
  footer += `<div class="fm-meta">Detected ${new Date(fault.dateTime).toLocaleString()}${logged}</div>`;

  return `<div class="fm-card fm-card--vehicle">${top}${body}${footer}</div>`;
}

/** Muted device-notification / telematics card (informational). */
export function buildDeviceNotificationCardHTML(fault: FaultData): string {
  return `<div class="fm-card fm-card--info">
    <div class="fm-card-top">
      <div class="fm-info-head">
        <span class="fm-info-tag">Info</span>
        <span class="fm-info-label">${escapeHtml(faultLabel(fault))}</span>
      </div>
      ${stateBadge(fault)}
    </div>
    <div class="fm-info-mode">Failure mode: ${escapeHtml(fault.failureMode?.name ?? 'Unknown')}</div>
    <div class="fm-meta">Detected ${new Date(fault.dateTime).toLocaleString()}</div>
  </div>`;
}
